import * as RadixDialog from '@radix-ui/react-dialog';
import { type MotionProps, motion, type PanInfo, useReducedMotion, type Variants } from 'motion/react';
import {
  type ComponentPropsWithoutRef,
  type MouseEvent,
  memo,
  type PointerEvent,
  type TouchEvent,
  useCallback,
} from 'react';

import { cn } from '@air/utils-tailwind';

const ACTION_SHEET_CONTENT_VARIANTS: Variants = {
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: 300,
  },
  initial: {
    opacity: 0,
    y: 300,
  },
};

const ACTION_SHEET_CONTENT_REDUCED_VARIANTS: Variants = {
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: 0,
  },
  initial: {
    opacity: 0,
    y: 0,
  },
};

export type ActionSheetContentProps = Omit<MotionProps, 'children'> &
  ComponentPropsWithoutRef<'div'> &
  Pick<RadixDialog.DialogContentProps, 'children'> & {
    onClose: () => void;
  };

export const ActionSheetContent = memo(({ children, className, onClose, ...restOfProps }: ActionSheetContentProps) => {
  const shouldReduceMotion = useReducedMotion();

  const onDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (info.offset.y > 40) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <motion.div
      animate="animate"
      className={cn(
        'fixed inset-x-0 top-auto -bottom-[5vh] z-portal flex max-h-[85vh] w-full rounded-t-8 bg-surface-1 pb-[5vh]',
        className,
      )}
      drag="y"
      dragConstraints={{ bottom: 0, top: 0 }}
      exit="exit"
      initial="initial"
      onDragEnd={onDragEnd}
      transition={{ bounce: 0.2, duration: 0.4, type: 'spring' }}
      variants={shouldReduceMotion ? ACTION_SHEET_CONTENT_REDUCED_VARIANTS : ACTION_SHEET_CONTENT_VARIANTS}
      {...restOfProps}
    >
      <RadixDialog.Content className="m-0 flex w-full flex-col gap-2 bg-transparent p-2">
        {children}
      </RadixDialog.Content>
    </motion.div>
  );
});

ActionSheetContent.displayName = 'ActionSheetContent';
